<template>
  <div id="wishlist-card">
    <template v-if="isNoPermission">
      <NoPermission />
    </template>

    <template v-else-if="isReadyPage">
      <div class="container-xl">
        <div class="page-header d-print-none">
          <div class="row g-2 align-items-center">
            <div class="col-sm">
              <WishHeader :wish-id="wishId" />
            </div>
            <div class="col-auto">
              <template v-if="showActions">
                <Actions />
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="page-body">
        <div class="container-xl">
          <div class="row">
            <div class="col-md-8">
              <div class="mb-3">
                <Title
                    :payload="{ title: formData.wish_title }"
                    @onChange="onChangeTitle"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Публичное описание</label>
                <Editor
                    @onChange="onChangeContentPublic"
                    :validations="validations"
                    :content="presenter.wish_content_public"
                    :entity_id="presenter.wish_id"
                    :show_bubble_menu="true"
                    :show_floating_menu="true"
                />
              </div>
            </div>

            <div class="col-md-4">
              <div v-if="showInfo" class="col-md-12 mb-2">
                <Info
                    :payload="{
                    user_name: presenter.userName,
                    wish_created_formatted: presenter.wishCreatedFormatted,
                    user_avatar: presenter.userAvatar(50, 50)
                  }"
                />
              </div>

              <div class="mb-2">
                <WishLinks
                    :initial-links="formData.wish_links"
                    :wish-id="wishId"
                    :disable="!isActiveWishLinks"
                    @on-change="onChangeWishLinks"
                />
              </div>

              <div class="mb-2">
                <Category
                    :payload="{ val: formData.wish_category_id, validations: validations }"
                    @onChange="onChangeCategoryId"
                />
              </div>

              <div class="mb-2">
                <div class="card">
                  <div class="card-body">
                    <div class="row">
                      <SelectThemes
                          v-model="formData.wish_themes"
                          mode="tags"
                          :searchable="true"
                          @change="onChangeThemes"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mb-2">
                <div class="card">
                  <div class="card-body">
                    <div class="row">
                      <Currency
                          v-model="formData.wish_currency_id"
                          mode="single"
                          :searchable="false"
                          default-image=""
                          @change="onChangeCurrency"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mb-2">
                <Budget
                    :payload="{ budget: formData.wish_budget }"
                    @onChange="onChangeBudget"
                />
              </div>

              <div class="mb-2">
                <Image
                    :payload="{ wish_id: wishId, image: wishQuery.image }"
                    :show-upload="showUploadImage"
                    @onChange="onChangeImage"
                />
              </div>

              <!-- Кнопки сохранения и удаления -->
              <div v-if="showSaveBtn" class="card mb-2">
                <div class="card-body">
                  <SaveWishButton
                      :is-new="isCreateWish"
                      :query="wishQuery"
                      @onSave="onSave"
                      :disabled="isSaveDisabled"
                  />
                </div>
              </div>

              <div v-if="showDeleteBtn" class="card mb-2">
                <div class="card-body">
                  <DeleteWishButton
                      :wish-id="wishId"
                      @onDelete="onDelete"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="page-body">
        <div class="container-xl">
          <div class="row">
            <div class="mb-3">
              <div class="loader-wishes">
                <div class="spinner-border"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, computed, defineOptions, provide } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useMeta } from "vue-meta"

// Импорт компонентов
import NoPermission from "@/modules/auth/pages/no_permission/Index"
import Title from "@/modules/wishlist/components/title_wish/Index"
import Editor from "@/components/editor/Editor"
import Category from "../../components/category/Index"
import SelectThemes from "@/modules/wishlist/components/themes/Index"
import Currency from "@/modules/wishlist/components/currency/Index"
import Budget from "@/modules/wishlist/components/budget/Index"
import SaveWishButton from "@/modules/wishlist/components/save_wish_btn/Index"
import DeleteWishButton from "@/modules/wishlist/components/delete_wish_btn/Index"
import Image from "@/modules/wishlist/components/image/Index"
import Info from "@/modules/wishlist/components/info/Index"
import WishHeader from "@/modules/wishlist/components/wish_header2/Index"
import Actions from "@/modules/wishlist/components/actions/Index"
import WishLinks from "@/modules/wishlist/components/wish_links/Index"

// Импорт сервисов и презентеров
import Api from "./Api"
import Notify from "@/services/Notify"
import { WishPresenter } from "@/modules/wishlist/pages/update/WishPresenter"

// Инициализация роутера
const route = useRoute()
const router = useRouter()

// Определение пропсов
defineProps({
  validations: {
    type: Object,
    default: () => ({})
  }
})

// Настройки опций компонента
defineOptions({
  name: "WishlistPageUpdateCreate"
})

// ------------------------------------------------------------------
// СОСТОЯНИЕ (STATE)
// ------------------------------------------------------------------
const wishId = ref(null)
const isReadyPage = ref(false)
const isNoPermission = ref(false)

const formData = reactive({
  wish_id: null,
  wish_title: null,
  wish_content_public: JSON.parse("{}"),
  wish_category_id: null,
  wish_themes: [],
  wish_created: null,
  wish_currency_id: 1,
  wish_user: {
    user_id: null,
    user_name: null,
    user_avatar_host: null,
    user_avatar_name: null,
  },
  wish_budget: null,
  wish_image: {
    host: null,
    name: null,
  },
  wish_delst: null,
  wish_links: [],
})

// ------------------------------------------------------------------
// ВЫЧИСЛЯЕМЫЕ СВОЙСТВА (COMPUTED / БЫВШИЕ HELPERS)
// ------------------------------------------------------------------
const isUpdateWish = computed(() => wishId.value !== null)
const isCreateWish = computed(() => wishId.value === null)

const isActiveWishLinks = computed(() => isUpdateWish.value && (formData.wish_delst === false || formData.wish_delst === null))
const showUploadImage   = computed(() => formData.wish_delst === false || formData.wish_delst === null)
const showSaveBtn       = computed(() => formData.wish_delst === false || formData.wish_delst === null)
const showDeleteBtn     = computed(() => formData.wish_delst === false)
const showActions       = computed(() => isUpdateWish.value)
const showInfo          = computed(() => isUpdateWish.value)

// Компьютеды для передачи данных в презентер и запросы
const presenter = computed(() => new WishPresenter(formData))

const wishQuery = computed(() => ({
  wish_id: formData.wish_id,
  currency_id: formData.wish_currency_id,
  theme_ids: formData.wish_themes,
  wish_name: formData.wish_title,
  wish_desc_json: JSON.stringify(formData.wish_content_public ?? "{}"),
  wish_amount: formData.wish_budget,
  category_id: formData.wish_category_id,
  image: {
    host: formData.wish_image?.host ?? null,
    name: formData.wish_image?.name ?? null,
  },
  wish_links: formData.wish_links
}))

// ------------------------------------------------------------------
// ЖИЗНЕННЫЙ ЦИКЛ И ЗАГРУЗКА ДАННЫХ
// ------------------------------------------------------------------
onMounted(async () => {
  if (route.params.id) {
    wishId.value = parseInt(route.params.id)
  }

  useMeta({
    title: isUpdateWish.value ? "Редактирование желания" : "Создание желания"
  })

  await loadPage()

  if (formData.wish_delst === true) {
    Notify.error("Это желание удалено")
  }
})

async function loadPage(loader = true) {
  if (loader) {
    isReadyPage.value = false
  }

  if (isUpdateWish.value) {
    try {
      const response = await Api.wish(wishId.value)
      setFromApi(response)
    } catch (error) {
      Notify.error("Не удалось загрузить данные желания")
    }
  }

  isReadyPage.value = true
}

// ------------------------------------------------------------------
// ТРАНСФОРМАЦИЯ ДАННЫХ (MAPPERS)
// ------------------------------------------------------------------
function setFromApi(data) {
  formData.wish_id = data?.wish_id ?? null
  formData.wish_title = data?.wish_name ?? ""
  formData.wish_content_public = getContentPublic(data)
  formData.wish_category_id = getCategory(data)
  formData.wish_themes = getThemes(data)
  formData.wish_created = data?.wish_created ?? []

  formData.wish_currency_id = data?.currency_id ?? []
  formData.wish_user = data?.user ?? []
  formData.wish_budget = data?.wish_amount ?? []
  formData.wish_image = {
    host: data?.wish_image_host ?? "",
    name: data?.wish_image_name ?? "",
  }
  formData.wish_delst = data?.wish_delst ?? false
  formData.wish_links = getLinks(data)
}

function getLinks(data) {
  const linksArray = data?.links
  if (!Array.isArray(linksArray)) return []
  return linksArray.map(link => ({
    link: link.link_name,
    shop_id: link.shop_id,
  }))
}

function getThemes(data) {
  const themesArray = data?.themes
  if (!Array.isArray(themesArray)) return []
  return themesArray.map(theme => theme.theme_id)
}

function getContentPublic(data) {
  const contentStr = data?.wish_desc_json
  if (!contentStr) return {}
  try {
    return JSON.parse(contentStr)
  } catch (e) {
    return {}
  }
}

function getCategory(data) {
  const category_id = data?.category_id ?? null
  return category_id <= 0 ? null : category_id
}

// ------------------------------------------------------------------
// ОБРАБОТЧИКИ СОБЫТИЙ (EVENTS)
// ------------------------------------------------------------------
const onChangeContentPublic = (content) => { formData.wish_content_public = content }
const onChangeCategoryId = (val) => { formData.wish_category_id = val }
const onChangeThemes = (val) => { formData.wish_themes = val }
const onChangeCurrency = (val) => { formData.wish_currency_id = val }
const onChangeTitle = (val) => { formData.wish_title = val }
const onChangeBudget = (val) => { formData.wish_budget = val }
async function onSave(val) {
  const savedWishId = val?.resp?.wish_id ?? null

  if (savedWishId === null) {
    Notify.error("Ошибка сохранения желания")
    return
  }

  if (val?.success === true) {
    Notify.success(isUpdateWish.value ? "Желание сохранено" : "Желание создано")
  } else {
    Notify.error("Ошибка сохранения")
  }

  if (isCreateWish.value) {
    wishId.value = savedWishId
    await loadPage()
    changeUrl(savedWishId)
  } else {
    await loadPage(false)
  }
}

async function onDelete(val) {
  if (val?.success === true) {
    Notify.success("Желание удалено")

    await loadPage()
  } else {
    Notify.error("Ошибка удаления желания")
  }
}

function onChangeImage(val) {
  if (val?.success === true) {
    Notify.success("Картинка обновлена")
    formData.wish_image = {
      host: val?.resp?.host ?? null,
      name: val?.resp?.name ?? null,
    }
  } else {
    Notify.error("Ошибка обновления картинки")
  }
}

function onChangeWishLinks(val) {
  if (val?.success === false) {
    Notify.error("Не удалось удалить ссылку")
    return
  }

  if (val?.api_delete === true) {
    Notify.success("Ссылка удалена")
  }

  formData.wish_links = val?.resp?.links ?? []
}

function changeUrl(id) {
  router.replace({
    name: 'WishlistWishUpdate',
    params: { id }
  })
}

// ------------------------------------------------------------------
// РЕЕСТР ВАЛИДАЦИИ (PROVIDE)
// ------------------------------------------------------------------
// Карта для хранения ошибок: ключ — ID поля (строка), значение — текст ошибки (или true)
const formErrors = ref(new Map())

// Передаем методы управления вниз по дереву компонентов
provide("wishlist-form-validation", {
  // Поле вызывает этот метод при изменении своего статуса валидации
  setFieldError: (fieldId, isValid, errorMessage = "") => {
    if (isValid) {
      formErrors.value.delete(fieldId) // Если всё хорошо — удаляем из карты ошибок
    } else {
      formErrors.value.set(fieldId, errorMessage || true) // Если ошибка — фиксируем
    }
  },
  // Если поле скрывается (v-if="false"), оно должно удалить себя из реестра
  unregisterField: (fieldId) => {
    formErrors.value.delete(fieldId)
  }
})

// Вычисляемое свойство: заблокировать кнопку, если есть хоть одна ошибка
const isSaveDisabled = computed(() => formErrors.value.size > 0)
</script>

<style src="./style.css" scoped></style>
